
import { Component, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { OuiSearchbar } from 'omnium-ui/searchbar';
import { OuiSelectFilter } from 'omnium-ui/select-filter';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import { ProblematicMetadataReport, ProblematicMetadataRow } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import {
  buildProblematicMetadataGroups,
  filterProblematicMetadataGroups,
  ProblematicMetadataGroup
} from '../../shared/problematic-metadata.utils';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import {
  SummaryGridComponent,
  SummaryGridItem
} from '../../shared/summary-grid/summary-grid.component';

@Component({
  selector: 'dbw-reporting-dashboard',
  imports: [
    FormsModule,
    MatTableModule,
    OuiButtonModule,
    OuiSearchbar,
    OuiSelectFilter,
    OuiTableModule,
    OuiTagModule,
    ConsultantCardComponent,
    InvestorCardComponent,
    InvestorStatusTagComponent,
    StateCardComponent,
    SummaryGridComponent
  ],
  templateUrl: './reporting-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reporting-dashboard.component.scss']
})
export class ReportingDashboardComponent implements OnInit {
  readonly groupedColumns = ['investor', 'consultant', 'detection', 'details'];
  readonly loading = signal(true);
  readonly exporting = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly metadataFilter = signal('');
  readonly selectedProblemKinds = signal<string[]>([]);
  readonly report = signal<ProblematicMetadataReport | undefined>(undefined);
  readonly problemTypeOptions = computed<OuiSelectOption<string>[]>(() => {
    const report = this.report();

    if (!report) {
      return [];
    }

    const optionsByKind = new Map<string, OuiSelectOption<string>>();

    [...report.corrections, ...report.anomalies].forEach(row => {
      const kind = this.getProblemKind(row);

      if (!kind || optionsByKind.has(kind)) {
        return;
      }

      optionsByKind.set(kind, {
        value: kind,
        label: row.detectionLabel?.trim() || kind
      });
    });

    return Array.from(optionsByKind.values()).sort((left, right) => left.label.localeCompare(right.label));
  });
  readonly summaryItems = computed<SummaryGridItem[]>(() => {
    const report = this.report();

    if (!report) {
      return [];
    }

    return [
      {
        key: 'transaction-mode',
        label: 'Mode transaction',
        value: report.status.transactionMode,
        meta: "Le reporting reflète le mode actif de l'API locale.",
        aside: {
          label: report.status.rollbackInsteadOfCommit ? 'Simulation' : 'Exécution',
          status: report.status.rollbackInsteadOfCommit ? 'pending' : 'completed'
        }
      },
      {
        key: 'corrections',
        label: 'Corrections candidates',
        value: report.correctionMetadataCount,
        meta: this.groupedCorrections().length + ' métadata(s) affichée(s) après filtrage.'
      },
      {
        key: 'anomalies',
        label: 'Anomalies',
        value: report.anomalyMetadataCount,
        meta: this.groupedAnomalies().length + ' métadata(s) affichée(s) après filtrage.'
      },
      {
        key: 'database-context',
        label: 'Contexte bases',
        value: report.status.atlasDatabase,
        compactValue: true,
        meta:
          'Minotaure: ' +
          report.status.minotaureDatabase +
          '.' +
          this.buildInMemoryFilterMessage()
      }
    ];
  });

  readonly groupedCorrections = computed(() =>
    this.buildMetadataGroups(
      this.report()?.corrections ?? [],
      this.metadataFilter(),
      this.selectedProblemKinds()
    ));

  readonly groupedAnomalies = computed(() =>
    this.buildMetadataGroups(
      this.report()?.anomalies ?? [],
      this.metadataFilter(),
      this.selectedProblemKinds()
    ));

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly router: Router,
    private readonly snackbarService: OuiSnackbarService
  ) {}

  ngOnInit(): void {
    this.loadReport();
  }

  reload(): void {
    this.loadReport();
  }

  openMetadata(metadataId: number): void {
    this.router.navigateByUrl(appRoutePaths.minotaure.metadata(metadataId));
  }

  openBatch(): void {
    this.router.navigateByUrl(appRoutePaths.minotaure.batch);
  }

  exportCsv(): void {
    if (this.exporting()) {
      return;
    }

    this.exporting.set(true);

    this.apiService.exportProblematicMetadataReport().subscribe({
      next: blob => {
        const downloadUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = `avancements-incoherents-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
        anchor.click();
        URL.revokeObjectURL(downloadUrl);
        this.exporting.set(false);
        this.snackbarService.open('Export CSV terminé.', 'success');
      },
      error: () => {
        this.exporting.set(false);
        this.snackbarService.open("L'export CSV a échoué.", 'error');
      }
    });
  }

  getDetectionTagStatus(kind: 'correction' | 'anomaly'): 'to-correct' | 'error' {
    return kind === 'correction' ? 'to-correct' : 'error';
  }

  private loadReport(): void {
    this.loading.set(true);
    this.error.set(undefined);

    this.apiService.getProblematicMetadataReport().subscribe({
      next: report => {
        this.report.set(report);
        this.loading.set(false);
      },
      error: error => {
        this.error.set(error?.error?.detail ?? 'Impossible de charger le reporting.');
        this.loading.set(false);
      }
    });
  }

  private buildMetadataGroups(
    rows: ProblematicMetadataRow[],
    filterValue: string,
    selectedProblemKinds: string[]
  ): ProblematicMetadataGroup[] {
    return filterProblematicMetadataGroups(
      this.filterGroupsByProblemKinds(buildProblematicMetadataGroups(rows), selectedProblemKinds),
      filterValue
    );
  }

  private filterGroupsByProblemKinds(
    groups: ProblematicMetadataGroup[],
    selectedProblemKinds: string[]
  ): ProblematicMetadataGroup[] {
    if (selectedProblemKinds.length === 0) {
      return groups;
    }

    const selectedKinds = new Set(selectedProblemKinds);

    return groups.filter(group =>
      group.rows.some(row => {
        const kind = this.getProblemKind(row);
        return !!kind && selectedKinds.has(kind);
      })
    );
  }

  private getProblemKind(row: ProblematicMetadataRow): string | null {
    return row.detectionKind?.trim() || row.detectionLabel?.trim() || null;
  }

  private buildInMemoryFilterMessage(): string {
    const problemTypeLabels = this.problemTypeOptions()
      .filter(option => option.value && this.selectedProblemKinds().includes(option.value))
      .map(option => option.label);
    const textFilter = this.metadataFilter().trim();
    const messages: string[] = [];

    if (problemTypeLabels.length > 0) {
      messages.push('Types: ' + problemTypeLabels.join(', '));
    }

    if (textFilter) {
      messages.push('Recherche: ' + textFilter);
    }

    return messages.length > 0
      ? ' Filtres in-memory actifs: ' + messages.join(' | ') + '.'
      : ' Aucun filtre in-memory actif.';
  }
}
